import { X } from "lucide-react";
import { ReactNode } from "react";

import { Button } from "@buildpy/ui/button";
import { Sheet, SheetClose, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@buildpy/ui/sheet";
import { lockedDescribedBy } from "@/modules/quote/ui/editor/locked-banner";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  // 제목 위 작은 줄(「철거 공사 · 3 / 6」).
  caption?: ReactNode;
  // 화면 낭독기용 한 줄 설명.
  description: string;
  // 닫기 앞 아이콘 단추(이전·다음 항목).
  actions?: ReactNode;
  footer?: ReactNode;
  // 넘기면 열 때 시트 자체로 포커스를 넣고 닫을 때 이 함수로 돌려준다 — 바깥 상태로 여는 시트는 Radix 가 돌아갈 Trigger 를 모른다.
  returnFocus?: () => void;
  // 잠긴 견적 — 본문 칸·단추를 끈다(머리줄 닫기·바닥 완료는 그대로).
  disabled?: boolean;
  children: ReactNode;
}

// 폰 편집기의 아래에서 올라오는 시트 한 벌 — 손잡이 · 머리줄 · 스크롤 본문 · 바닥 단추줄(맨 아래 줄이 홈 표시줄만큼 띄운다).
const PhoneSheet = ({ open, onOpenChange, title, caption, description, actions, footer, returnFocus, disabled, children }: Props) => (
  <Sheet open={open} onOpenChange={onOpenChange}>
    <SheetContent
      className="flex max-h-[calc(100dvh-4.5rem)] flex-col gap-0 p-0"
      side="bottom"
      onCloseAutoFocus={(e) => {
        if (!returnFocus) return;
        e.preventDefault();
        returnFocus();
      }}
      onOpenAutoFocus={(e) => {
        // 첫 칸에는 넣지 않는다(폰 키보드가 바로 올라온다) — returnFocus 가 있으면 시트 자체로.
        e.preventDefault();
        if (returnFocus) (e.currentTarget as HTMLElement | null)?.focus({ preventScroll: true });
      }}>
      <span aria-hidden className="mx-auto mt-2 h-1 w-10 shrink-0 rounded-full bg-border" />
      <SheetHeader className="shrink-0 gap-x-1 px-4 pb-2 pt-1">
        <div className="flex min-w-0 flex-1 flex-col">
          {caption && <span className="truncate text-xs text-muted-foreground">{caption}</span>}
          <SheetTitle className="text-base">{title}</SheetTitle>
          <SheetDescription className="sr-only">{description}</SheetDescription>
        </div>
        {actions}
        <SheetClose asChild>
          <Button aria-label="닫기" className="shrink-0" size="icon" variant="ghost">
            <X />
          </Button>
        </SheetClose>
      </SheetHeader>
      <div className={`flex min-h-0 flex-1 flex-col overflow-y-auto px-4 ${footer ? "pb-4" : "pb-safe-4"}`}>
        <fieldset className="flex min-w-0 flex-col gap-y-3.5" aria-describedby={lockedDescribedBy(disabled)} data-testid="phone-sheet-body" disabled={disabled}>
          {children}
        </fieldset>
      </div>
      {footer && <div className="flex shrink-0 items-center gap-2 border-t border-border px-4 pb-safe-3 pt-3">{footer}</div>}
    </SheetContent>
  </Sheet>
);

export default PhoneSheet;
